type ThrottleStatus = {
  maximumAvailable: number;
  currentlyAvailable: number;
  restoreRate: number;
};

type LimiterOptions = {
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  maxThrottleRetries?: number;
  maxWaitMs?: number;
  maxRetryJitterMs?: number;
  random?: () => number;
};

type ShopifyResponse = {
  extensions?: unknown;
  errors?: unknown;
  status?: unknown;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const DEFAULT_MAX_WAIT_MS = 8_000;
const DEFAULT_MAX_RETRY_JITTER_MS = 250;
const SAFE_THROTTLE_RETRY_OPERATIONS = new Set([
  "calculateDraftOrder",
  "createDraftOrder",
  "completeDraftOrder",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getShopifyResponse(value: unknown): ShopifyResponse | null {
  if (!isRecord(value)) {
    return null;
  }

  if ("response" in value && isRecord(value.response)) {
    return value.response;
  }

  return value;
}

function getThrottleStatus(value: unknown): ThrottleStatus | null {
  const response = getShopifyResponse(value);
  if (!response || !isRecord(response.extensions) || !isRecord(response.extensions.cost)) {
    return null;
  }

  const throttleStatus = response.extensions.cost.throttleStatus;
  if (
    !isRecord(throttleStatus) ||
    typeof throttleStatus.maximumAvailable !== "number" ||
    typeof throttleStatus.currentlyAvailable !== "number" ||
    typeof throttleStatus.restoreRate !== "number"
  ) {
    return null;
  }

  return throttleStatus as ThrottleStatus;
}

function getRequestedQueryCost(value: unknown): number | null {
  const response = getShopifyResponse(value);
  if (!response || !isRecord(response.extensions) || !isRecord(response.extensions.cost)) {
    return null;
  }

  const requestedQueryCost = response.extensions.cost.requestedQueryCost;
  return typeof requestedQueryCost === "number" ? requestedQueryCost : null;
}

function isShopifyThrottleError(error: unknown) {
  const response = getShopifyResponse(error);
  if (!response) {
    return false;
  }

  if (response.status === 429) {
    return true;
  }

  if (!Array.isArray(response.errors)) {
    return false;
  }

  return response.errors.some((graphqlError) =>
    isRecord(graphqlError) &&
    isRecord(graphqlError.extensions) &&
    graphqlError.extensions.code === "THROTTLED",
  );
}

/**
 * Coordinates Admin API calls within one warm function instance. Shopify's
 * throttle state is shared more broadly than this instance, so every response
 * updates the local view and an explicit throttle pauses subsequent calls.
 */
export function createShopifyAdminRequestLimiter({
  now = Date.now,
  sleep: wait = sleep,
  maxThrottleRetries = 2,
  maxWaitMs = DEFAULT_MAX_WAIT_MS,
  maxRetryJitterMs = DEFAULT_MAX_RETRY_JITTER_MS,
  random = Math.random,
}: LimiterOptions = {}) {
  let previousRequest: Promise<void> = Promise.resolve();
  let latestThrottleStatus: (ThrottleStatus & { observedAt: number }) | null = null;
  let maximumObservedQueryCost = 0;
  let cooldownUntil = 0;

  function observe(responseOrError: unknown) {
    const throttleStatus = getThrottleStatus(responseOrError);
    const requestedQueryCost = getRequestedQueryCost(responseOrError);
    if (requestedQueryCost !== null) {
      maximumObservedQueryCost = Math.max(maximumObservedQueryCost, requestedQueryCost);
    }
    if (throttleStatus) {
      latestThrottleStatus = {...throttleStatus, observedAt: now()};
    }

    if (!isShopifyThrottleError(responseOrError)) {
      return;
    }

    const recoveryCost = Math.max(maximumObservedQueryCost, requestedQueryCost ?? 0, 1);
    const recoveryDelayMs = throttleStatus && throttleStatus.restoreRate > 0
      ? Math.ceil(Math.max(0, recoveryCost - throttleStatus.currentlyAvailable) / throttleStatus.restoreRate * 1000)
      : 1000;
    // Shopify recommends a one-second backoff when a request is throttled.
    cooldownUntil = Math.max(cooldownUntil, now() + Math.max(1000, recoveryDelayMs));
  }

  async function waitForCapacity(additionalDelayMs = 0) {
    const currentTime = now();
    let delayMs = Math.max(0, cooldownUntil - currentTime);

    if (latestThrottleStatus && maximumObservedQueryCost > 0 && latestThrottleStatus.restoreRate > 0) {
      const restoredPoints = latestThrottleStatus.currentlyAvailable +
        (currentTime - latestThrottleStatus.observedAt) / 1000 * latestThrottleStatus.restoreRate;
      const availablePoints = Math.min(latestThrottleStatus.maximumAvailable, restoredPoints);
      const capacityDelayMs = Math.ceil(
        Math.max(0, maximumObservedQueryCost - availablePoints) /
        latestThrottleStatus.restoreRate * 1000,
      );
      delayMs = Math.max(delayMs, capacityDelayMs);
    }

    if (delayMs > 0) {
      // Keep enough function runtime for the Shopify operation and a bounded
      // retry. A later explicit THROTTLED response will extend the cooldown.
      await wait(Math.min(delayMs + additionalDelayMs, maxWaitMs));
    }
  }

  async function run<T>(operationName: string, operation: () => Promise<T>) {
    await waitForCapacity();

    for (let attempt = 0; ; attempt += 1) {
      try {
        return await operation();
      } catch (error) {
        observe(error);
        const canRetry =
          isShopifyThrottleError(error) &&
          SAFE_THROTTLE_RETRY_OPERATIONS.has(operationName) &&
          attempt < maxThrottleRetries;
        if (!canRetry) {
          throw error;
        }

        console.warn("Retrying Shopify Admin API request after throttling", {
          operationName,
          attempt: attempt + 1,
        });
        // Spread retries from independently scaled functions so they do not
        // all repeat the same request at Shopify's one-second boundary.
        const retryJitterMs = Math.floor(random() * maxRetryJitterMs);
        await waitForCapacity(retryJitterMs);
      }
    }
  }

  function queue<T>(operationName: string, operation: () => Promise<T>) {
    const request = previousRequest.catch(() => undefined).then(() => run(operationName, operation));
    previousRequest = request.then(() => undefined, () => undefined);
    return request;
  }

  return {queue, observe};
}

const shopifyAdminRequestLimiter = createShopifyAdminRequestLimiter();

export const queueShopifyAdminRequest = shopifyAdminRequestLimiter.queue;
export const observeShopifyAdminResponse = shopifyAdminRequestLimiter.observe;
